import { useState } from 'react';
import { COURAGES, OU } from '@nextilt/commun';
import {
  COMBIEN_DE_TEMPS,
  DES_QUELLE_HEURE,
  ET_TU_ES_PLUTOT,
  JE_CHOISIS,
  JE_CHOISIS_NOTE,
  LA_QUESTION,
  MAINTENANT_DEBUT,
  MOTS_COURAGE,
  MOTS_OU_DU_JOUR,
  MOTS_TEMPS_DE_LA_QUESTION,
  OU_A,
  OU_SERAS_TU,
  PROPOSE_MOI,
  PROPOSE_MOI_NOTE,
  RIEN_DE_PREVU_AUJOURDHUI,
  RIEN_DE_PREVU_CE_JOUR,
} from '../../partage/vocabulaire';
import { heureLisible } from './datesDeLAgenda';
import { heureTapee } from './journeeEnCours';
import { debutsProposes, motDuDebut, type Reponses } from './questionDuJour';
import { Curseur, LigneQuiOuvre, ListeDeChoix } from '../../partage/PiecesDuCarnet';
import './LaQuestion.css';

/**
 * La question avant de planifier un jour, au téléphone (chantier `telephone-journee-agenda`,
 * ticket 01, décisions d'Igor du 2026-10-05, 7, 8 et 20 ; maquette `design/telephone.html`,
 * piste « Journée », « La question ») : combien de temps, quel courage, dès quelle heure, où,
 * puis deux choix, « Propose-moi une journée » ou « Je choisis moi-même ».
 *
 * Rien que le rendu : les réponses, leurs valeurs par défaut et ce qui est proposé viennent de
 * `questionDuJour.ts` ; ce qui suit les deux choix, de `PlanifierUnJour`.
 */
export function LaQuestion({
  aujourdhui,
  maintenant,
  reponses,
  onRepondre,
  onProposeMoi,
  onJeChoisis,
  phrase,
}: {
  aujourdhui: boolean;
  /** La phrase de tête, à la place de « Rien n'est prévu… » (« Recommencer », ticket 04). */
  phrase?: string;
  /** L'heure qu'il est, arrondie par le serveur ; aujourd'hui seulement. */
  maintenant: string | null;
  reponses: Reponses;
  onRepondre: (r: Partial<Reponses>) => void;
  onProposeMoi: () => void;
  onJeChoisis: () => void;
}) {
  const [deplie, setDeplie] = useState<'debut' | 'ou' | null>(null);

  return (
    <section className="question" aria-label={LA_QUESTION}>
      <p className="question-phrase">{phrase ?? (aujourdhui ? RIEN_DE_PREVU_AUJOURDHUI : RIEN_DE_PREVU_CE_JOUR)}</p>

      {/* Plus de pastilles (chantier telephone-habit, 03) : des curseurs à crans, des lignes qui ouvrent leur liste. */}
      <div className="question-carte">
        <Curseur
          titre={COMBIEN_DE_TEMPS}
          crans={MOTS_TEMPS_DE_LA_QUESTION}
          choisi={reponses.tempsMin}
          attribut="temps"
          onChoisir={(v) => onRepondre({ tempsMin: v })}
        />
        <Curseur
          titre={ET_TU_ES_PLUTOT}
          crans={COURAGES.map((c) => [c, MOTS_COURAGE[c]] as const)}
          choisi={reponses.courage}
          attribut="courage"
          onChoisir={(c) => onRepondre({ courage: c })}
        />

        <div className="question-lignes">
          <LigneQuiOuvre
            titre={DES_QUELLE_HEURE}
            valeur={motDuDebut(reponses.debut, maintenant)}
            ouverte={deplie === 'debut'}
            onOuvrir={() => setDeplie(deplie === 'debut' ? null : 'debut')}
            attributs={{ 'data-reglage': 'debut' }}
          />
          {deplie === 'debut' && (
            <div className="question-liste">
              <ListeDeChoix
                nom={DES_QUELLE_HEURE}
                choix={debutsProposes(maintenant).map((d) => [d, d === maintenant ? MAINTENANT_DEBUT : heureLisible(d)] as const)}
                choisi={reponses.debut}
                attribut="debut"
                onChoisir={(d) => {
                  onRepondre({ debut: d });
                  setDeplie(null);
                }}
              />
              <label className="question-autre-heure">
                {OU_A}
                {/* Sans `key` : la frappe ne remonte pas le champ ; un début choisi replie la liste. */}
                <input
                  type="time"
                  step={300}
                  defaultValue={reponses.debut}
                  aria-label={`${DES_QUELLE_HEURE}, ${OU_A.toLocaleLowerCase('fr-FR')}`}
                  onChange={(e) => {
                    const h = heureTapee(e.currentTarget.value, e.currentTarget.validity.badInput);
                    if (h) onRepondre({ debut: h });
                  }}
                />
              </label>
            </div>
          )}
          <LigneQuiOuvre
            titre={OU_SERAS_TU}
            valeur={MOTS_OU_DU_JOUR[reponses.ou]}
            ouverte={deplie === 'ou'}
            onOuvrir={() => setDeplie(deplie === 'ou' ? null : 'ou')}
            attributs={{ 'data-reglage': 'ou' }}
          />
          {deplie === 'ou' && (
            <ListeDeChoix
              nom={OU_SERAS_TU}
              choix={OU.map((v) => [v, MOTS_OU_DU_JOUR[v]] as const)}
              choisi={reponses.ou}
              attribut="ou"
              onChoisir={(v) => {
                onRepondre({ ou: v });
                setDeplie(null);
              }}
            />
          )}
        </div>
      </div>

      <div className="question-choix">
        <button type="button" className="pouce-act est-principal question-fort" data-choix="propose" onClick={onProposeMoi}>
          {PROPOSE_MOI}
          <small>{PROPOSE_MOI_NOTE}</small>
        </button>
        <button type="button" className="pouce-act question-trait" data-choix="choisis" onClick={onJeChoisis}>
          {JE_CHOISIS}
          <small>{JE_CHOISIS_NOTE}</small>
        </button>
      </div>
    </section>
  );
}
